import { BarChart3, Users } from "lucide-react"
import { useStore } from "zustand"

import type { GuildWarRosterBoard } from "@/lib/guild-war-api"
import type { RosterPageUiStore } from "@/lib/stores/roster-page-ui-store"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { GUILD_WAR_PATH_REGISTRY } from "@/core/guild-war/path-registry"
import {
  getGuildWarRosterTeamKeys,
  getGuildWarTeamCapacity,
  getGuildWarTeamRosterMembers,
} from "@/core/guild-war/roster-board"
import {
  GUILD_WAR_TEAM_DISPLAY,
  type GuildWarRosterTeamKey,
} from "@/core/guild-war/types"
import { toGuildWarPathDisplayLabel } from "@/lib/guild-war-path-display"
import { useRosterIsCompositionOpen } from "@/lib/stores/roster-page-ui-store"

export function collectBoardMemberIds(board: GuildWarRosterBoard) {
  return [
    ...getGuildWarRosterTeamKeys(board).flatMap((teamKey) => {
      const team = board[teamKey]
      return team ? getGuildWarTeamRosterMembers(team) : []
    }),
    ...board.commanders,
    ...board.halftimePerformers,
  ].filter((value): value is string => Boolean(value))
}

export function RosterCompositionCard({
  store,
  board,
}: {
  store: RosterPageUiStore
  board: GuildWarRosterBoard
}) {
  const isOpen = useRosterIsCompositionOpen(store)
  const setIsOpen = useStore(store, (s) => s.setIsRosterCompositionOpen)

  return (
    <Card className="overflow-hidden border-border/60 bg-card/70">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-xl">Roster Composition</CardTitle>
              <CardDescription>
                Team slot coverage and martial path distribution for this
                published snapshot.
              </CardDescription>
            </div>
            <CollapsibleTrigger className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground">
              {isOpen ? "Collapse" : "Expand"}
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <RosterCompositionSummary board={board} />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

type TeamKey = GuildWarRosterTeamKey

type TeamCompositionRow = {
  key: TeamKey
  label: string
  filled: number
  capacity: number
}

function RosterCompositionSummary({ board }: { board: GuildWarRosterBoard }) {
  const teamRows: TeamCompositionRow[] = getGuildWarRosterTeamKeys(
    board,
  ).flatMap((key) => {
    const team = board[key]
    return team
      ? [
          {
            key,
            label: GUILD_WAR_TEAM_DISPLAY[key].label,
            filled: getGuildWarTeamRosterMembers(team).length,
            capacity: getGuildWarTeamCapacity(team),
          },
        ]
      : []
  })

  const assignedMemberIds = Array.from(new Set(collectBoardMemberIds(board)))
  const primaryPathCounts = new Map<string, number>()

  for (const memberId of assignedMemberIds) {
    const primaryPathCode = board.memberPaths[memberId]?.primaryPathCode ?? null
    const label =
      toGuildWarPathDisplayLabel(primaryPathCode, {
        includeEmoji: true,
        fallback: "Unassigned",
      }).trim() || "Unassigned"
    primaryPathCounts.set(label, (primaryPathCounts.get(label) ?? 0) + 1)
  }

  const primaryPathRows = GUILD_WAR_PATH_REGISTRY.map((path) => ({
    label:
      toGuildWarPathDisplayLabel(path.code, {
        includeEmoji: true,
        fallback: "Unassigned",
      }).trim() || path.shortLabel,
    count:
      primaryPathCounts.get(
        toGuildWarPathDisplayLabel(path.code, {
          includeEmoji: true,
          fallback: "Unassigned",
        }).trim() || path.shortLabel,
      ) ?? 0,
    code: path.code,
  }))

  const totalAssigned = assignedMemberIds.length

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="grid content-start gap-3 rounded-2xl border border-border/70 bg-background/55 p-4">
        <div className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <BarChart3 className="size-4 text-primary" />
          Team coverage
        </div>
        <div className="grid gap-3">
          {teamRows.map((team) => {
            const ratio = team.capacity ? team.filled / team.capacity : 0
            const width = `${Math.round(ratio * 100)}%`

            return (
              <div key={team.key} className="grid gap-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{team.label}</span>
                  <span className="text-muted-foreground">
                    {team.filled}/{team.capacity}
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-muted/60">
                  <div
                    className="h-full rounded-full bg-primary/80"
                    style={{ width }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <section className="grid content-start gap-3 rounded-2xl border border-border/70 bg-background/55 p-4">
        <div className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <Users className="size-4 text-primary" />
          Path distribution
        </div>
        {totalAssigned === 0 ? (
          <p className="text-sm text-muted-foreground">
            No assigned members in this published roster yet.
          </p>
        ) : (
          <div className="grid gap-2.5">
            {primaryPathRows.map((row) => {
              const ratio = totalAssigned > 0 ? row.count / totalAssigned : 0
              const width = `${Math.round(ratio * 100)}%`

              return (
                <div key={row.code} className="grid gap-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{row.label}</span>
                    <span className="text-muted-foreground">
                      {row.count} ({Math.round(ratio * 100)}%)
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-muted/60">
                    <div
                      className="h-full rounded-full bg-accent/80"
                      style={{ width }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
